import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
import AccountPopover from './AccountPopover';
import NotificationsPopover from './NotificationsPopover';
import useAuth from '../../../hooks/useAuth';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive('up', 'lg');
  const { setting } = useAuth();

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 },
        }}
      >
        {isDesktop && verticalLayout && <Logo sx={{ mr: 2.5 }} />}

        {!isDesktop && (
          <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
          {/* Two WhatsApp Buttons for different numbers */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButtonAnimate
              component="a"
              href={`https://wa.me/${setting?.general?.app_whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: '#25D366',
                bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#fff'),
                border: '1.5px solid #25D366',
                mr: 0.5,
                animation: 'pulseWhatsApp 1.8s infinite',
                transition: 'box-shadow 0.3s',
                boxShadow: '0 0 0 0 rgba(37,211,102,0.2)',
                '&:hover': {
                  bgcolor: '#25D366',
                  color: '#fff',
                  boxShadow: '0 0 8px 2px rgba(37,211,102,0.25)',
                },
              }}
              title="WhatsApp Support 1"
            >
              <WhatsAppIcon fontSize="medium" />
            </IconButtonAnimate>
            {
              // <IconButtonAnimate
              //   component="a"
              //   href={`https://wa.me/${setting?.general?.app_whatsapp2}`}
              //   target="_blank"
              //   rel="noopener noreferrer"
              //   sx={{
              //     color: '#128C7E',
              //     bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#fff'),
              //     border: '1.5px solid #128C7E',
              //     mr: 0.5,
              //     animation: 'pulseWhatsApp2 1.8s infinite',
              //     transition: 'box-shadow 0.3s',
              //     boxShadow: '0 0 0 0 rgba(18,140,126,0.2)',
              //     '&:hover': {
              //       bgcolor: '#128C7E',
              //       color: '#fff',
              //       boxShadow: '0 0 8px 2px rgba(18,140,126,0.25)',
              //     },
              //   }}
              //   title="WhatsApp Support 2"
              // >
              //   <WhatsAppIcon fontSize="medium" />
              // </IconButtonAnimate>
            }
            <style>{`
              @keyframes pulseWhatsApp {
                0% { box-shadow: 0 0 0 0 rgba(37,211,102,0.2); }
                70% { box-shadow: 0 0 0 8px rgba(37,211,102,0); }
                100% { box-shadow: 0 0 0 0 rgba(37,211,102,0.2); }
              }
              @keyframes pulseWhatsApp2 {
                0% { box-shadow: 0 0 0 0 rgba(18,140,126,0.2); }
                70% { box-shadow: 0 0 0 8px rgba(18,140,126,0); }
                100% { box-shadow: 0 0 0 0 rgba(18,140,126,0.2); }
              }
            `}</style>
          </Box>
          <NotificationsPopover />
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
